import { fastTravelFuelCost, warpFuelCost } from "./fuel";
import { provisionsPerHour } from "./provisions";
import type { NpcHullTierLabel } from "./types";

/**
 * How far and how long a ship can keep going (16.103, Don's direction).
 *
 * **The consumption model was already built.** Fuel has been real since 16.50 and
 * Provisions since Section 11 / 16.62. What was missing is the thing Don actually
 * asked for: *"how does the player know how much fuel and provision to bring and
 * how long they will last?"* Nowhere in the game answered that. The pools were
 * visible; the rates that drain them were not.
 *
 * So this module derives nothing new. It reads the existing rates and turns them
 * into the four numbers a captain is actually asking:
 *
 * - what one jump costs
 * - how many jumps are left in the tank
 * - what the crew eat per hour
 * - how many hours of eating are left
 *
 * It lives in one place because it is shown in five (Engine module, Fleet Roster,
 * Crew Quarters, Cargo, and the Handbook), and five screens each doing their own
 * division is five chances to disagree.
 *
 * ## One naming decision, and it is a correction
 *
 * Don asked whether Fast Travel consumes fuel, and said that if it does not,
 * "Fuel" should be renamed "Warp Fuel". **It does.** 16.50 is explicit that fuel
 * gates *both* travel actions: a Fast Travel costs `base x hullMultiplier` and a
 * Warp costs 5x that. Only Move To, orbiting, scanning and combat are free.
 *
 * So the rename would make things worse, not better -- it would tell the captain
 * that the in-system jump is free when it is the one they make most. The fix is
 * the readout, not the label: `fuelSummary` reports both costs side by side, so
 * what consumes fuel is stated rather than implied by its name.
 */

export interface FuelSummary {
  /** Cost of one in-system Fast Travel for this hull. */
  fastTravelCost: number;
  /** Cost of one Warp through a Gate to another system. */
  warpCost: number;
  onBoard: number;
  capacity: number;
  /** Whole Fast Travels remaining in the tank. */
  fastTravelsLeft: number;
  /** Whole Warps remaining in the tank. */
  warpsLeft: number;
  /** True when the tank cannot even manage one Fast Travel -- the stranded case
   *  the Distress Signal (16.54) exists to rescue. */
  stranded: boolean;
}

export function fuelSummary(hullTierLabel: NpcHullTierLabel, onBoard: number, capacity: number): FuelSummary {
  const fastTravelCost = fastTravelFuelCost(hullTierLabel);
  const warpCost = warpFuelCost(hullTierLabel);
  return {
    fastTravelCost,
    warpCost,
    onBoard,
    capacity,
    fastTravelsLeft: fastTravelCost > 0 ? Math.floor(onBoard / fastTravelCost) : 0,
    warpsLeft: warpCost > 0 ? Math.floor(onBoard / warpCost) : 0,
    stranded: onBoard < fastTravelCost
  };
}

export interface ProvisionsSummary {
  crew: number;
  perHour: number;
  onBoard: number;
  capacity: number;
  /** Hours of game clock before the crew run out. Infinity for zero crew. */
  hoursLeft: number;
  /** True inside the last few hours -- see PROVISIONS_LOW_HOURS for why not a day. */
  low: boolean;
}

/**
 * Below this many hours remaining, the readout starts warning.
 *
 * Six, not twenty-four, and the live view is what caught it: a FULL tank is
 * exactly 24 hours (startingProvisionsCapacity = 24h of consumption), so a
 * 24-hour threshold lit the warning on a brand-new captain and never went out.
 * A warning that is always on is not a warning. Six hours is a quarter tank --
 * late enough to mean something, early enough to still reach a Station.
 */
export const PROVISIONS_LOW_HOURS = 6;

export function provisionsSummary(crew: number, onBoard: number, capacity: number): ProvisionsSummary {
  const perHour = provisionsPerHour(crew);
  const hoursLeft = perHour > 0 ? onBoard / perHour : Number.POSITIVE_INFINITY;
  return { crew, perHour, onBoard, capacity, hoursLeft, low: hoursLeft < PROVISIONS_LOW_HOURS };
}

/**
 * "3 d 4 h", "18 h", "40 min" -- whichever unit the number is actually in.
 *
 * Provisions endurance spans minutes to weeks across the roster, so a single
 * unit is either unreadable at one end or imprecise at the other.
 */
export function formatEndurance(hours: number): string {
  if (!Number.isFinite(hours)) return "—";
  if (hours <= 0) return "0 h";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)} h`;
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours - days * 24);
  return rest > 0 ? `${days} d ${rest} h` : `${days} d`;
}

/**
 * The one line that answers "can I get home?" -- shown on the Engine module.
 *
 * Deliberately phrased in JUMPS rather than fuel units, because the decision the
 * captain is making is "how many more places can I go", and no player converts
 * 350 fuel into that in their head mid-flight.
 */
export function fuelHeadline(s: FuelSummary): string {
  if (s.stranded) return "Stranded — not enough fuel to travel";
  if (s.fastTravelsLeft === 0) return "No jumps left";
  const warps = s.warpsLeft > 0 ? `, or ${s.warpsLeft} warp${s.warpsLeft === 1 ? "" : "s"}` : ", no warps";
  return `${s.fastTravelsLeft} jump${s.fastTravelsLeft === 1 ? "" : "s"}${warps}`;
}
